import type { ProductNoEdibleInfo } from "@dransay/api";
import { CATEGORIES } from "@dransay/api/models";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { translateCountryName } from "~/utils/translate-country";
import { filterSchemaParser } from "./filters/utils";

type ProductDetailsCTAInfoBoxProps = {
	product: ProductNoEdibleInfo;
};

export function ProductDetailsCTAInfoBox({
	product,
}: ProductDetailsCTAInfoBoxProps) {
	const { producer, countryOfOrigin, irradiated } = product;
	const { t, i18n } = useTranslation();

	const [translatedCountry, setTranslatedCountry] = useState<string | null>(
		null,
	);
	useEffect(() => {
		// Clear any previously-translated country first so navigating between
		// products in the same mounted component never leaks a stale value
		// when the new product has no `countryOfOrigin`.
		if (!countryOfOrigin) {
			setTranslatedCountry(null);
			return;
		}

		let cancelled = false;
		translateCountryName(countryOfOrigin, i18n.language).then((value) => {
			if (!cancelled) setTranslatedCountry(value);
		});
		return () => {
			cancelled = true;
		};
	}, [countryOfOrigin, i18n.language]);

	const producerUrl = useMemo(
		() =>
			`/products?filters=${filterSchemaParser.serialize({ producers: [Number(producer.id)] })}`,
		[producer.id],
	);

	const firstInfoItem = useMemo(() => {
		if (product.category === CATEGORIES.EDIBLES) {
			return (
				<ProductDetailsCTAInfoBoxItem
					label={t("package", { ns: "product" })}
					value={t("nPieces", { ns: "common", amount: 10 })}
				/>
			);
		}

		if (product.category === CATEGORIES.DRINKABLES) {
			return (
				<ProductDetailsCTAInfoBoxItem
					label={t("package", { ns: "product" })}
					value={t("nPieces", { ns: "common", amount: 6 })}
				/>
			);
		}

		if (product.category === CATEGORIES.CAPSULES) {
			return (
				<ProductDetailsCTAInfoBoxItem
					label={t("package", { ns: "product" })}
					value={t("nPieces", { ns: "common", amount: 10 })}
				/>
			);
		}

		return (
			<ProductDetailsCTAInfoBoxItem
				label={t("irradiation", { ns: "product" })}
				value={t(irradiated ? "yes" : "no", { ns: "common" })}
			/>
		);
	}, [product.category, irradiated, t]);

	return (
		<div className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-x-10 md:gap-y-2 text-body-sm p-3 md:px-6 bg-gray-100 rounded-md">
			{firstInfoItem}

			<ProductDetailsCTAInfoBoxItem
				label={t("country")}
				value={translatedCountry ?? (countryOfOrigin || t("notAvailableAbbr"))}
			/>

			<ProductDetailsCTAInfoBoxItem
				label={
					product.category === CATEGORIES.EDIBLES ||
					product.category === CATEGORIES.DRINKABLES ||
					product.category === CATEGORIES.CAPSULES
						? t("producerStartingSubstance", { ns: "common" })
						: t("producer", { ns: "common" })
				}
				value={producer.name || ""}
				link={producerUrl}
			/>
		</div>
	);
}

function ProductDetailsCTAInfoBoxItem({
	label,
	value,
	link,
}: {
	label: string;
	value: string;
	link?: string;
}) {
	return (
		<div className="flex flex-col">
			<span className="text-gray-500 text-body-sm">{label}</span>

			{link ? (
				<Link to={link} className="text-gray-700 text-body-sm underline">
					{value}
				</Link>
			) : (
				<span className="text-gray-700 text-body-sm">{value}</span>
			)}
		</div>
	);
}
